// frontend/src/components/Orders/CreateOrderModal.jsx
import React, { useState, useEffect } from 'react';
import { X, Search, Plus, Minus, Trash2, ShoppingBag, User, MapPin, Package } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { useAuth } from '../../hooks/useAuth';

const CreateOrderModal = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  // اطلاعات مشتری
  const [customerSearch, setCustomerSearch] = useState('');
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerList, setShowCustomerList] = useState(false);

  // محصولات
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [showProductList, setShowProductList] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [cartItems, setCartItems] = useState([]);

  // ========== پکیج‌ها ==========
  const [packages, setPackages] = useState([]);
  const [packageSearch, setPackageSearch] = useState('');
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showPackageList, setShowPackageList] = useState(false);

  // اطلاعات ارسال
  const [orderData, setOrderData] = useState({
    shipping_address: '',
    recipient_name: '',
    recipient_phone: '',
    city: '',
    province: '',
    postal_code: '',
    note: '',
    payment_method: 'manual',
    is_manual_price: true
  });

  // جستجوی مشتریان
  useEffect(() => {
    if (customerSearch.length > 1) {
      const searchCustomers = async () => {
        try {
          const response = await api.get(`/accounts/users/?search=${customerSearch}&role=customer`);
          setCustomers(response.data.results || []);
          setShowCustomerList(true);
        } catch (error) {
          console.error('Error searching customers:', error);
        }
      };
      const timeout = setTimeout(searchCustomers, 500);
      return () => clearTimeout(timeout);
    } else {
      setCustomers([]);
      setShowCustomerList(false);
    }
  }, [customerSearch]);

  // جستجوی محصولات
  useEffect(() => {
    if (productSearch.length > 1) {
      const searchProducts = async () => {
        try {
          const response = await api.get(`/products/products/?search=${productSearch}&status=active`);
          setProducts(response.data.results || []);
          setShowProductList(true);
        } catch (error) {
          console.error('Error searching products:', error);
        }
      };
      const timeout = setTimeout(searchProducts, 500);
      return () => clearTimeout(timeout);
    } else {
      setProducts([]);
      setShowProductList(false);
    }
  }, [productSearch]);

  // ========== جستجوی پکیج‌ها ==========
  useEffect(() => {
    if (packageSearch.length > 1) {
      const searchPackages = async () => {
        try {
          const response = await api.get(`/products/packages/?search=${packageSearch}&is_active=true`);
          setPackages(response.data.results || []);
          setShowPackageList(true);
        } catch (error) {
          console.error('Error searching packages:', error);
        }
      };
      const timeout = setTimeout(searchPackages, 500);
      return () => clearTimeout(timeout);
    } else {
      setPackages([]);
      setShowPackageList(false);
    }
  }, [packageSearch]);

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch(customer.full_name || customer.phone || customer.username);
    setShowCustomerList(false);
  };

  const handleAddProduct = () => {
    if (!selectedProduct) return;

    const existingItem = cartItems.find(item => item.product_id === selectedProduct.id);
    const defaultPrice = selectedProduct.show_price_on_call ? 0 : selectedProduct.final_price;

    if (existingItem) {
      setCartItems(cartItems.map(item =>
        item.product_id === selectedProduct.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCartItems([
        ...cartItems,
        {
          product_id: selectedProduct.id,
          product_name: selectedProduct.name,
          price: defaultPrice,
          quantity: 1,
          is_price_on_call: selectedProduct.show_price_on_call || false,
          max_stock: selectedProduct.stock || 999,
          has_price: !selectedProduct.show_price_on_call && selectedProduct.final_price > 0
        }
      ]);
    }

    setSelectedProduct(null);
    setProductSearch('');
    setShowProductList(false);
  };

  // ========== تابع افزودن پکیج به سفارش با قابلیت قیمت توافقی ==========
  const handleAddPackage = () => {
    if (!selectedPackage) return;

    // اضافه کردن محصولات پکیج به cartItems
    const packageProducts = selectedPackage.items || [];
    if (packageProducts.length === 0) {
      toast.error('این پکیج شامل هیچ محصولی نیست');
      return;
    }

    // تشخیص اینکه پکیج "تماس برای قیمت" دارد یا خیر
    const isPriceOnCall = selectedPackage.show_price_on_call || false;
    const defaultPrice = isPriceOnCall ? 0 : (selectedPackage.final_price || selectedPackage.total_price || 0);

    const newItems = packageProducts.map(item => ({
      product_id: item.product,
      product_name: item.product_name || 'محصول',
      price: defaultPrice,
      quantity: item.quantity || 1,
      is_price_on_call: isPriceOnCall,  // ✅ اگر پکیج بدون قیمت است، این true باشد
      max_stock: 999,
      has_price: !isPriceOnCall && defaultPrice > 0,
      is_package_item: true,
      package_id: selectedPackage.id,
      package_name: selectedPackage.name
    }));

    setCartItems(prev => [...prev, ...newItems]);
    setSelectedPackage(null);
    setPackageSearch('');
    setShowPackageList(false);

    if (isPriceOnCall) {
      toast.warning(`${selectedPackage.name} بدون قیمت است. لطفاً قیمت توافقی را وارد کنید`);
    } else {
      toast.success(`${selectedPackage.name} به سفارش اضافه شد`);
    }
  };

  const handleRemoveProduct = (productId) => {
    setCartItems(cartItems.filter(item => item.product_id !== productId));
  };

  const handleQuantityChange = (productId, change) => {
    setCartItems(cartItems.map(item => {
      if (item.product_id === productId) {
        const newQuantity = Math.max(1, item.quantity + change);
        if (newQuantity <= item.max_stock) {
          return { ...item, quantity: newQuantity };
        }
        toast.error(`موجودی کافی نیست (حداکثر ${item.max_stock})`);
        return item;
      }
      return item;
    }));
  };

  const handlePriceChange = (productId, newPrice) => {
    setCartItems(cartItems.map(item =>
      item.product_id === productId
        ? { ...item, price: parseFloat(newPrice) || 0 }
        : item
    ));
  };

  const calculateTotal = () => {
    return cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const validateOrder = () => {
    if (!selectedCustomer) {
      toast.error('لطفاً مشتری را انتخاب کنید');
      return false;
    }

    if (cartItems.length === 0) {
      toast.error('حداقل یک محصول یا پکیج انتخاب کنید');
      return false;
    }

    if (!orderData.shipping_address) {
      toast.error('لطفاً آدرس را وارد کنید');
      return false;
    }

    // ✅ بررسی قیمت توافقی برای آیتم‌های بدون قیمت (محصول یا پکیج)
    const missingPriceItems = cartItems.filter(item =>
      item.is_price_on_call && (!item.price || item.price <= 0)
    );

    if (missingPriceItems.length > 0) {
      toast.error(
        `لطفاً قیمت توافقی را برای موارد زیر وارد کنید:\n${missingPriceItems.map(i =>
          i.is_package_item ? `📦 ${i.package_name} - ${i.product_name}` : i.product_name
        ).join(', ')}`
      );
      return false;
    }

    // ✅ بررسی قیمت صفر برای آیتم‌های معمولی (اختیاری)
    const zeroPriceItems = cartItems.filter(item =>
      !item.is_price_on_call && (!item.price || item.price <= 0) && !item.is_package_item
    );

    if (zeroPriceItems.length > 0) {
      toast.error(`قیمت محصولات زیر صفر است. لطفاً اصلاح کنید:\n${zeroPriceItems.map(i => i.product_name).join(', ')}`);
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateOrder()) {
      return;
    }

    setLoading(true);

    try {
      // ✅ ساخت payload با پشتیبانی از پکیج
      const orderPayload = {
        user_id: selectedCustomer.id,
        items: cartItems.map(item => ({
          product_id: item.product_id,
          package_id: item.is_package_item ? item.package_id : null,  // ✅ ارسال package_id
          quantity: item.quantity,
          price: item.price
        })),
        shipping_address: orderData.shipping_address,
        recipient_name: orderData.recipient_name || selectedCustomer.full_name || selectedCustomer.username,
        recipient_phone: orderData.recipient_phone || selectedCustomer.phone,
        city: orderData.city || '',
        province: orderData.province || '',
        postal_code: orderData.postal_code || '',
        note: orderData.note || '',
        payment_method: orderData.payment_method || 'manual',
        is_manual_price: true
      };

      console.log('📦 Sending order payload:', orderPayload);

      const response = await api.post('/orders/create-by-seller/', orderPayload);

      console.log('✅ Order response:', response.data);

      toast.success('سفارش با موفقیت ثبت شد');
      onSuccess && onSuccess(response.data);

      // ریست کردن فرم
      setCartItems([]);
      setSelectedCustomer(null);
      setCustomerSearch('');
      setOrderData({
        shipping_address: '',
        recipient_name: '',
        recipient_phone: '',
        city: '',
        province: '',
        postal_code: '',
        note: '',
        payment_method: 'manual',
        is_manual_price: true
      });

      onClose();

    } catch (error) {
      console.error('❌ Error creating order:', error);

      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);

        if (error.response.data && typeof error.response.data === 'object') {
          const errors = error.response.data;
          const errorMessages = [];

          if (errors.items && Array.isArray(errors.items)) {
            errors.items.forEach(msg => errorMessages.push(msg));
          }

          if (errors.user_id) {
            errorMessages.push(`مشتری: ${errors.user_id}`);
          }

          if (errors.non_field_errors) {
            errorMessages.push(errors.non_field_errors);
          }

          if (errorMessages.length === 0) {
            errorMessages.push(JSON.stringify(errors));
          }

          toast.error(`خطا در ثبت سفارش:\n${errorMessages.join('\n')}`);
        } else {
          toast.error(error.response.data?.detail || 'خطا در ثبت سفارش');
        }
      } else if (error.request) {
        toast.error('خطا در ارتباط با سرور');
      } else {
        toast.error('خطا: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" onClick={onClose}>
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShoppingBag className="w-6 h-6 text-blue-600" />
              <h3 className="text-lg font-bold text-gray-900">ثبت سفارش جدید</h3>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
            {/* انتخاب مشتری */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <User className="w-4 h-4 inline-block ml-1" />
                انتخاب مشتری <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="جستجوی مشتری (نام، شماره تماس)..."
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {showCustomerList && customers.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {customers.map(customer => (
                      <div
                        key={customer.id}
                        onClick={() => handleSelectCustomer(customer)}
                        className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                      >
                        <div className="font-medium">{customer.full_name || customer.username}</div>
                        <div className="text-sm text-gray-500">{customer.phone}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {selectedCustomer && (
                <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white font-bold">
                      {selectedCustomer.full_name?.[0] || selectedCustomer.username?.[0]}
                    </div>
                    <div>
                      <div className="font-medium">{selectedCustomer.full_name || selectedCustomer.username}</div>
                      <div className="text-sm text-gray-500">{selectedCustomer.phone}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* انتخاب پکیج */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Package className="w-4 h-4 inline-block ml-1" />
                افزودن پکیج
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder="جستجوی پکیج..."
                    value={packageSearch}
                    onChange={(e) => setPackageSearch(e.target.value)}
                    onFocus={() => setShowPackageList(true)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  {/* // ========== نمایش پکیج‌ها در جستجو ========== */}
                  {showPackageList && packages.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {packages.map(pkg => (
                        <div
                          key={pkg.id}
                          onClick={() => setSelectedPackage(pkg)}
                          className={`p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 ${selectedPackage?.id === pkg.id ? 'bg-blue-50' : ''
                            }`}
                        >
                          <div className="font-medium">
                            {pkg.name}
                            {pkg.show_price_on_call && (
                              <span className="text-xs text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full mr-2">
                                🔹 تماس برای قیمت
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            {pkg.items?.length || 0} محصول • {pkg.power_display || `${pkg.power_rating}kW`}
                            {!pkg.show_price_on_call && pkg.final_price && (
                              <span className="mr-2">• {pkg.final_price.toLocaleString()} تومان</span>
                            )}
                            {pkg.show_price_on_call && (
                              <span className="mr-2 text-orange-500">• قیمت توافقی</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={handleAddPackage}
                  disabled={!selectedPackage}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* انتخاب محصولات */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Plus className="w-4 h-4 inline-block ml-1" />
                افزودن محصول <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder="جستجوی محصول..."
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {showProductList && products.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {products.map(product => (
                        <div
                          key={product.id}
                          onClick={() => setSelectedProduct(product)}
                          className={`p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 ${selectedProduct?.id === product.id ? 'bg-blue-50' : ''
                            }`}
                        >
                          <div className="font-medium">{product.name}</div>
                          <div className="text-sm text-gray-500">
                            {product.show_price_on_call ? (
                              '🔹 تماس برای قیمت'
                            ) : (
                              `${product.final_price?.toLocaleString() || 0} تومان`
                            )}
                          </div>
                          <div className="text-xs text-gray-400">موجودی: {product.stock || 0}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={handleAddProduct}
                  disabled={!selectedProduct}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* لیست محصولات انتخاب شده */}
            {cartItems.length > 0 && (
              <div className="mb-6">
                <h4 className="font-medium text-gray-700 mb-3">محصولات انتخاب شده ({cartItems.length})</h4>
                <div className="space-y-3">
                  {cartItems.map(item => (
                    <div key={item.product_id || item.package_id} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <div className="font-medium">
                          {item.product_name}
                          {item.is_package_item && (
                            <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full mr-2">
                              {item.package_name}
                            </span>
                          )}
                        </div>
                        {item.is_price_on_call ? (
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-sm text-orange-600 font-medium">قیمت توافقی:</span>
                            <input
                              type="number"
                              value={item.price || ''}
                              onChange={(e) => handlePriceChange(item.product_id, e.target.value)}
                              placeholder="قیمت را وارد کنید"
                              className="w-32 p-1 border border-gray-300 rounded text-sm"
                            />
                            <span className="text-xs text-red-500">*الزامی</span>
                          </div>
                        ) : (
                          <div className="text-sm text-gray-500">
                            قیمت: {item.price?.toLocaleString() || 0} تومان
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleQuantityChange(item.product_id, -1)}
                          className="p-1 hover:bg-gray-200 rounded"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-8 text-center font-medium">{item.quantity}</span>
                        <button
                          onClick={() => handleQuantityChange(item.product_id, 1)}
                          className="p-1 hover:bg-gray-200 rounded"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      <button
                        onClick={() => handleRemoveProduct(item.product_id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                  <div className="flex justify-between font-bold">
                    <span>جمع کل:</span>
                    <span>{calculateTotal().toLocaleString()} تومان</span>
                  </div>
                </div>
              </div>
            )}

            {/* اطلاعات ارسال */}
            <div className="mb-6">
              <h4 className="font-medium text-gray-700 mb-3">
                <MapPin className="w-4 h-4 inline-block ml-1" />
                اطلاعات ارسال <span className="text-red-500">*</span>
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="نام گیرنده"
                  value={orderData.recipient_name}
                  onChange={(e) => setOrderData({ ...orderData, recipient_name: e.target.value })}
                  className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="tel"
                  placeholder="شماره تماس گیرنده"
                  value={orderData.recipient_phone}
                  onChange={(e) => setOrderData({ ...orderData, recipient_phone: e.target.value })}
                  className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  placeholder="استان"
                  value={orderData.province}
                  onChange={(e) => setOrderData({ ...orderData, province: e.target.value })}
                  className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  placeholder="شهر"
                  value={orderData.city}
                  onChange={(e) => setOrderData({ ...orderData, city: e.target.value })}
                  className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  placeholder="کد پستی"
                  value={orderData.postal_code}
                  onChange={(e) => setOrderData({ ...orderData, postal_code: e.target.value })}
                  className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <select
                  value={orderData.payment_method}
                  onChange={(e) => setOrderData({ ...orderData, payment_method: e.target.value })}
                  className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="manual">ثبت دستی</option>
                  <option value="cash_on_delivery">پرداخت در محل</option>
                  <option value="card_to_card">کارت به کارت</option>
                  <option value="online">پرداخت آنلاین</option>
                </select>
                <textarea
                  placeholder="آدرس کامل"
                  value={orderData.shipping_address}
                  onChange={(e) => setOrderData({ ...orderData, shipping_address: e.target.value })}
                  className="col-span-2 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows="2"
                />
                <textarea
                  placeholder="یادداشت (اختیاری)"
                  value={orderData.note}
                  onChange={(e) => setOrderData({ ...orderData, note: e.target.value })}
                  className="col-span-2 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows="2"
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              انصراف
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || cartItems.length === 0 || !selectedCustomer}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition-colors flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  در حال ثبت...
                </>
              ) : (
                <>
                  <ShoppingBag className="w-4 h-4" />
                  ثبت سفارش
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateOrderModal;